import { useState } from "react";
import { fmtShortDate } from "../../lib/format";
import { aiSelf } from "../../lib/session";
import { Caption, Icon, Lead, List, ListRow, SectionHeader, Sheet } from "../../components/ui/ds";
import "./insights-sheet.css";

// v31 §4.3 — zoznam VŠETKÝCH dnešných postrehov (výslovná požiadavka majiteľky).
//
// Bublina na Dnes ukáže jednu vetu; tento sheet ukáže všetko, čo appka dnes v dátach našla,
// a hlavne: **akcie, ktoré sú dostupné vždy**. Režim prežitia si musí vedieť zapnúť aj vtedy,
// keď ho AI nedetegovala — appka nesmie byť jediná, kto rozhoduje, kedy je toho na človeka veľa.
//
// Texty položiek chodia hotové zo servera (deterministická slovenská kopírka
// `insights_cards.py`) — tu sa nič neskladá a nič sa neprekladá, aby sa čísla nemohli
// rozísť s tým, čo o nich hovorí kouč.

// vrstva postrehu → ikona. Zámerne bez farieb: rovnaký pokojný riadok pre problém aj
// pochvalu (farebné „červené" karty by z prehľadu spravili zoznam obvinení).
const LAYER_ICON = { problem: "target", pattern: "chart", positive: "thumbsup", review: "calendar" };
const ACTION_ICON = { start_recovery: "shield", cleanup: "folder", ask_ai: "ai" };

function InsightCard({ item, onAction, onDecline }) {
  return (
    <ListRow
      lead={<Lead size="sm" tone="outline">
        <Icon name={LAYER_ICON[item.layer] || "bulb"} size={16} color="currentColor" />
      </Lead>}
      title={item.title}
      meta={<>
        <span className="bz-insights__text">{item.text}</span>
        {item.action && (
          <span className="bz-insight-actions">
            <button type="button" className="bz-insight-action"
              onClick={() => onAction(item.action, item.type)}>{item.action.label}</button>
            {/* odmietnutie musí byť rovnako dostupné ako prijatie — inak sa ponuky zbavíš
                len tým, že ju vykonáš (rovnaké pravidlo ako v bubline) */}
            <button type="button" className="bz-insight-decline"
              onClick={() => onDecline(item.type)}>Teraz nie</button>
          </span>
        )}
      </>} />
  );
}

export function InsightsSheet({ data, onAction, onDecline, onClose }) {
  // „Skôr som si všimla/všimol" je zbalené: je to archív, nie to, kvôli čomu sem človek prišiel.
  // v32 — asistent tu hovorí o SEBE, takže tvar sa skloňuje podľa rodu ZVOLENEJ postavy
  // (nie podľa rodu používateľa). Predtým bolo natvrdo ženské „všimla" aj pri Ďoďovi.
  const [histOpen, setHistOpen] = useState(false);
  const items = (data && data.items) || [];
  const actions = (data && data.actions) || [];
  const history = (data && data.history) || [];

  return (
    <Sheet icon="sparkles" title="Postrehy" onClose={onClose}>
      <SectionHeader label="Dnes" count={items.length} style={{ marginTop: 2 }} />
      {!data ? (
        <Caption variant="note">Načítavam…</Caption>
      ) : items.length === 0 ? (
        <Caption variant="note">Dnes appka v tvojich dátach nič nové nenašla. To je v poriadku —
          vzorce potrebujú čas.</Caption>
      ) : (
        <List className="bz-insights__list">
          {items.map((it) => (
            <InsightCard key={`${it.type}:${it.habit_id || 0}`} item={it}
              onAction={onAction} onDecline={onDecline} />
          ))}
        </List>
      )}

      {actions.length > 0 && (<>
        <SectionHeader label="Čo môžeš urobiť" />
        <List className="bz-insights__list">
          {actions.map((a) => (
            <ListRow key={a.kind} onClick={() => onAction(a, null)}
              lead={<Lead size="sm" tone="outline">
                <Icon name={ACTION_ICON[a.kind] || "bulb"} size={16} color="currentColor" />
              </Lead>}
              title={a.label}
              trailing={<span className="bz-insights__chev">
                <Icon name="chevron" size={16} color="currentColor" />
              </span>} />
          ))}
        </List>
      </>)}

      {history.length > 0 && (<>
        <SectionHeader label={`Skôr som si ${aiSelf("všimol", "všimla")}`} count={history.length}
          onClick={() => setHistOpen((v) => !v)}
          trailing={<span className={`bz-insights__chev${histOpen ? " bz-insights__chev--open" : ""}`}>
            <Icon name="chevron" size={16} color="currentColor" />
          </span>} />
        {histOpen && (
          <List className="bz-insights__list">
            {history.map((h) => (
              <ListRow key={`${h.type}:${h.day}`} title={h.text} meta={fmtShortDate(h.day)} />
            ))}
          </List>
        )}
      </>)}
    </Sheet>
  );
}
